import { useState, useEffect } from 'react';
import {
  Modal, Tabs, Input, Button, Table, Checkbox,
  Space, Typography, Divider, message, Spin, Tag,
} from 'antd';
import {
  UserAddOutlined, SearchOutlined,
  UserOutlined, BankOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { childApi } from '../../../api/child.api';
import { registrationApi } from '../../../api/registration.api';
import useDebounce from '../../../hooks/useDebounce';
import ChildForm from '../../child/components/ChildForm';
import { getApiErrorMessage } from '../../../utils/helpers';

const { Text } = Typography;

const AddChildrenModal = ({ open, onClose, eventId, event, defaultTab, onSuccess }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || 'new');

  // Tab 1: Add New Child
  const [newChildLoading, setNewChildLoading] = useState(false);
  const [formResetKey,    setFormResetKey]    = useState(0);

  // Tab 2: Search & Assign
  const [searchQuery,      setSearchQuery]      = useState('');
  const [selectedChildren, setSelectedChildren] = useState([]);
  const debouncedSearch = useDebounce(searchQuery, 350);

  // School the event belongs to — used to scope child search results
  const schoolId   = event?.school?.id || event?.school?._id || (typeof event?.school === 'string' ? event.school : null);
  const schoolName = event?.school?.name || null;

  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab || 'new');
      setSearchQuery('');
      setSelectedChildren([]);
      setFormResetKey((k) => k + 1);
    }
  }, [open, defaultTab]);

  // ── Search query (Tab 2) — scoped to the event's school ─────
  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ['children-search-modal', debouncedSearch, schoolId],
    queryFn:  () => {
      if (/^\d{6,}/.test(debouncedSearch.trim())) {
        return childApi.getByParentPhone(debouncedSearch.trim(), schoolId);
      }
      return childApi.search(debouncedSearch.trim(), schoolId);
    },
    enabled: debouncedSearch.trim().length >= 2,
  });

  const searchResults = (searchData?.data || []).filter(Boolean);

  // ── Tab 1: Add New Child ─────────────────────────────────────
  const handleAddNewChild = async (values) => {
    const schoolId = event?.school?.id || event?.school?._id || String(event?.school || '');
    setNewChildLoading(true);
    try {
      let payload = values;
      if (values instanceof FormData) {
        values.set('school', schoolId);
      } else {
        payload = { ...values, school: schoolId };
      }
      const childRes = await childApi.create(payload);
      const childId  = childRes.data?.id || childRes.data?._id;
      await registrationApi.register({ event: eventId, child: childId, school: schoolId });
      message.success('Child added and registered successfully!');
      setFormResetKey((k) => k + 1);
      onSuccess?.();
    } catch (err) {
      message.error(getApiErrorMessage(err) || 'Failed to add child');
    } finally {
      setNewChildLoading(false);
    }
  };

  // ── Tab 2: Bulk assign ───────────────────────────────────────
  const bulkAssignMutation = useMutation({
    mutationFn: () => registrationApi.bulkRegister({ eventId, childIds: selectedChildren }),
    onSuccess:  (res) => {
      const { registered = 0, alreadyRegistered = 0 } = res.data || {};
      message.success(
        `${registered} child${registered !== 1 ? 'ren' : ''} registered!` +
        (alreadyRegistered > 0 ? ` (${alreadyRegistered} already registered)` : '')
      );
      setSelectedChildren([]);
      setSearchQuery('');
      onSuccess?.();
    },
    onError: (err) => message.error(err.message),
  });

  const toggleChild = (id, checked) => {
    setSelectedChildren((prev) =>
      checked ? [...prev, id] : prev.filter((i) => i !== id)
    );
  };

  // ── Search results columns (Tab 2) ──────────────────────────
  const searchCols = [
    {
      key:   'select',
      width: 44,
      render: (_, r) => {
        const id = r.id || r._id;
        return (
          <Checkbox
            checked={selectedChildren.includes(id)}
            onChange={(e) => toggleChild(id, e.target.checked)}
          />
        );
      },
    },
    {
      title: 'Child',
      render: (_, r) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{r.name}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'capitalize' }}>
              {r.gender}{r.age != null ? ` · ${r.age} yrs` : ''}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Parent / Phone',
      render: (_, r) => (
        <div>
          <Text style={{ fontSize: 12 }}>{r.parent?.name || '—'}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>📞 {r.parent?.phone || '—'}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'School',
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 11 }}>{r.school?.name || '—'}</Text>
      ),
    },
  ];

  const tabItems = [
    {
      key:      'new',
      label:    <span><UserAddOutlined /> Add New</span>,
      children: (
        <div style={{ paddingTop: 8 }}>
          <ChildForm
            asModal={false}
            hideSchool={true}
            open={open && activeTab === 'new'}
            onSubmit={handleAddNewChild}
            loading={newChildLoading}
            resetKey={formResetKey}
          />
        </div>
      ),
    },

    {
      key:      'assign',
      label:    <span><SearchOutlined /> Search & Assign</span>,
      children: (
        <div style={{ paddingTop: 8 }}>
          {schoolName && (
            <div style={{ marginBottom: 10 }}>
              <Tag
                icon={<BankOutlined />}
                style={{
                  background:   'var(--wl-primary-pale)',
                  color:        'var(--wl-primary)',
                  border:       '1px solid var(--wl-primary-pale)',
                  borderRadius: 9999,
                  fontWeight:   600,
                  fontSize:     12,
                  padding:      '3px 12px',
                }}
              >
                Showing children from: {schoolName}
              </Tag>
            </div>
          )}
          <Input
            prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)' }} />}
            placeholder="Search by child name or parent phone…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{ borderRadius: 9999, marginBottom: 12 }}
          />

          {debouncedSearch.trim().length >= 2 ? (
            searchLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            ) : (
              <>
                <Table
                  dataSource={searchResults}
                  columns={searchCols}
                  rowKey={(r) => r.id || r._id}
                  size="small"
                  pagination={false}
                  scroll={{ y: 200 }}
                  locale={{ emptyText: 'No children found' }}
                />
                {selectedChildren.length > 0 && (
                  <>
                    <Divider style={{ margin: '10px 0' }} />
                    <Button
                      type="primary"
                      block
                      loading={bulkAssignMutation.isPending}
                      onClick={() => bulkAssignMutation.mutate()}
                      style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
                    >
                      Assign {selectedChildren.length} Selected Child{selectedChildren.length !== 1 ? 'ren' : ''}
                    </Button>
                  </>
                )}
              </>
            )
          ) : (
            <div style={{ textAlign: 'center', padding: 32, color: '#ccc' }}>
              <UserOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                {debouncedSearch.length > 0
                  ? 'Type at least 2 characters to search'
                  : 'Search by name or parent phone number'}
              </Text>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <Space>
          <UserAddOutlined style={{ color: 'var(--wl-primary)' }} />
          <span>Add Children to Event</span>
        </Space>
      }
      width={640}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginTop: 4 }}
      />
    </Modal>
  );
};

export default AddChildrenModal;
